<template>
    <div>
        <div class="bg-white/75 p-4 rounded-lg mt-4 shadow-79 shadow-amazon-500/15">
            <div v-for="(item, key) in extractOptions">
                <WidgetsProductVariantSelector
                    :name="key"
                    :options="item"
                    v-model="selectedVariantDetails"
                    @update="handleVariantUpdate"
                />
            </div>
            <div>
                <PagesProductAmount filter="تعداد" v-model="amount" :options="productQuantityObject" />
            </div>
            <div class="mt-16 flex items-center justify-between" v-if="activeVariant?.price ?? 0 > 0">
                <ClientOnly>
                    <WidgetsProductSpecialPrices :active-price="activeVariant" />
                </ClientOnly>
                <UButton
                    v-if="!isInCart"
                    :loading="isLoading"
                    :size="isDesktop ? 'lg' : 'md'"
                    icon="i-solar-cart-5-line-duotone"
                    @click="handleAddToCart"
                >
                    افزودن به سبد خرید
                </UButton>
                <UButton v-else :size="isDesktop ? 'lg' : 'md'" variant="outline" to="/cart">
                    <div class="flex flex-col items-center">
                        <p class="text-xs">
                            <span>{{ (isInCart as ICart).quantity }} عدد</span>
                            <span class="text-gray-600"> در سبد خرید شما </span>
                        </p>
                        <span class="font-bold text-sm"> مشاهده سبد خرید </span>
                    </div>
                </UButton>
            </div>
            <div v-else class="mt-16 flex items-center justify-center">
                <UButton block variant="outline" :size="isDesktop ? 'lg' : 'md'" icon="i-solar-cart-5-line-duotone">
                    تماس بگیرید
                </UButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { max } from "jalali-moment";
import type { ModelRef } from "vue";
import type { ICart } from "~/libs/types/api/cart";
import type { IPrices, IPricesVariant } from "~/libs/types/api/product";
import type { IDynamicKey } from "~/libs/types/general";

const emits = defineEmits(["addToCart"]);

const { width } = useWindowSize();

const isDesktop = computed(() => {
    return width.value > 1024;
});

const toast = useToast();
const { data: cart } = useCart({ immediate: true });

const props = defineProps({
    max_cart: Number,
    min_cart: Number,
    variants: Array as PropType<IPrices[]>,
});

onMounted(() => {
    selectedVariantId.value = props.variants?.[0].variants?.[0]?.id;
});

const { refresh: refreshCart } = useCart();

const amount = ref(1);
const isLoading = ref(false);
const selectedVariantId: ModelRef<string | undefined> = defineModel();
const selectedVariantDetails: Ref<IDynamicKey<any>> = ref({});

const minCartVariants = computed(() => {
    if (amount.value === 1) return props.variants;
    return useFilter(props.variants, (item) => (item.max_cart ?? 100) > amount.value || item.max_cart === 0);
});

function handleVariantUpdate(oldValue: any, newValue: any) {
    if (!isEqual(oldValue, newValue)) {
        const allVariants = useFlatMap(minCartVariants.value, "variants");
        const newVariants = { ...selectedVariantDetails.value, ...newValue };
        selectedVariantId.value = findClosestVariant(allVariants, newVariants, newValue);
    }
}

const activeVariant: ComputedRef<IPrices | undefined> = computed(() => {
    if (minCartVariants.value && selectedVariantId.value) {
        interface IActive {
            index: number;
            variant: IPricesVariant;
        }
        const active: IActive | null = findActiveVariantAndIndex(
            minCartVariants.value,
            selectedVariantId.value
        ) as IActive;
        if (active?.variant?.items)
            selectedVariantDetails.value = useTransform(active.variant.items, (result, item) => {
                result[item.name] = item.value;
            });
        return minCartVariants.value[active?.index];
    }
    return minCartVariants.value![0];
});

async function handleAddToCart() {
    isLoading.value = true;
    await fetchData("/cart/add", {
        useSanctum: true,
        method: "POST",
        body: {
            product_variant_id: selectedVariantId.value,
            quantity: amount.value,
        },
        onResponse({ response }) {
            if (isResponseSuccessful(response.status)) {
                emits("addToCart");
                toast.add({
                    description: "کالا به سبد خرید شما اضافه شد",
                    icon: "i-solar-cart-5-line-duotone",
                    timeout: 5000,
                    actions: [
                        {
                            label: "مشاهده سبد خرید و پرداخت",
                            click: () => {
                                navigateTo("/cart");
                            },
                        },
                    ],
                });
                refreshCart();
            }
        },
    }).finally(() => {
        isLoading.value = false;
    });
}

const isInCart: ComputedRef<boolean | ICart> = computed(() => {
    const tmp = useFind(cart.cartItems, ["variant.id", selectedVariantId.value]);
    if (tmp) return tmp;
    return false;
});

const extractOptions = computed(() => {
    // Extract and flatten all variants arrays from the data
    const allVariants = useFlatMap(minCartVariants.value, "variants");

    // Flatten all items across all variants and group by the 'name' field
    const grouped = useGroupBy(useFlatMap(allVariants, "items"), "name");

    // Map the grouped items to extract unique 'items' values for each name
    const result = useMapValues(grouped, (items, name) => {
        if (name === "وزن") {
            // Handle special case for "وزن"
            return useUniqBy(items, "value").map((item) => {
                const value = item.value;

                // Find the first variant with this "وزن" value that has a thumb
                const variantWithThumb = useFind(
                    allVariants,
                    (variant) => useSome(variant.items, { name: "وزن", value: value }) && !isEmpty(variant.thumb)
                );

                // Get the thumb URL or set it to null if not available
                const thumb = variantWithThumb ? useValues(variantWithThumb.thumb)[0]?.original_url || null : null;

                return {
                    value,
                    thumb,
                };
            });
        } else {
            // Handle other names (non-"وزن")
            return useUniq(useMap(items, "value"));
        }
    });

    return result;
});

function findActiveVariantAndIndex(objects: any, variantId: any) {
    let result = null;

    objects.map((obj: any, index: number) => {
        const foundVariant = useFind(obj.variants, { id: variantId });
        if (foundVariant) {
            result = { index, variant: foundVariant };
            return true;
        }
    });

    return result as any;
}

const productQuantityObject = {
    min: props.min_cart ?? 1,
    max: props.max_cart ?? 100,
};
</script>

<style></style>
